// src/components/Leaderboard.tsx - Digital Duel Leaderboard
import React, { useState, useEffect } from 'react';
import { ddApi } from '../lib/supabaseClient';

interface LeaderboardData {
  players: Array<{
    username: string;
    faction_id: number;
    games_played: number;
    games_won: number;
  }>;
  factionStats: {
    defender_wins: number;
    attacker_wins: number;
  };
}

const Leaderboard: React.FC = () => {
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const leaderboardData = await ddApi.getLeaderboardData();
      setData(leaderboardData);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getFactionName = (factionId: number) => {
    return factionId === 1 ? 'Defender' : 'Attacker';
  };

  const getFactionColor = (factionId: number) => {
    return factionId === 1 ? 'text-blue-400' : 'text-red-400';
  };

  const getFactionIcon = (factionId: number) => {
    return factionId === 1 ? '🛡️' : '⚔️';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-400">Loading leaderboard...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">⚠️ {error || 'No data available'}</div>
        <button
          onClick={loadLeaderboard}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  if (data.players.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        </div>
        <h3 className="text-xl font-medium text-white mb-2">No Games Completed Yet</h3>
        <p className="text-gray-400">The leaderboard will appear once players finish their first games!</p>
      </div>
    );
  }

  const totalWins = data.factionStats.defender_wins + data.factionStats.attacker_wins;

  return (
    <div className="space-y-6">
      {/* Header with Faction Battle Status */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-yellow-400 mb-2">🏆 Digital Duel Leaderboard</h2>
          <p className="text-gray-300">Hall of Champions</p>
        </div>

        {/* Faction Battle Status */}
        <div className="bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-center text-white mb-4">⚔️ Faction Battle Status</h3>
          <div className="flex justify-center items-center space-x-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">
                🛡️ {data.factionStats.defender_wins}
              </div>
              <div className="text-sm text-gray-300">Defender Wins</div>
            </div>
            
            <div className="text-4xl text-gray-500">vs</div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-red-400">
                ⚔️ {data.factionStats.attacker_wins}
              </div>
              <div className="text-sm text-gray-300">Attacker Wins</div>
            </div>
          </div>
          
          {totalWins > 0 && (
            <div className="text-center mt-4 text-sm text-gray-400">
              Total Battles: {totalWins}
            </div>
          )}
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">🥇 Champions Ranking</h3>
          <p className="text-sm text-gray-400 mt-1">Ranked by total victories</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Player
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Faction
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Played
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Won
                </th>
				<th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
  Win Rate
</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {data.players.map((player, index) => {
                const rank = index + 1;
                const isTopThree = rank <= 3;
                
                return (
                  <tr 
                    key={`${player.username}-${index}`}
                    className={`hover:bg-gray-750 transition-colors ${
                      isTopThree ? 'bg-gray-750' : ''
                    }`}
                  >
                    {/* Rank */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-lg font-bold ${
                          rank === 1 ? 'text-yellow-400' :
                          rank === 2 ? 'text-gray-300' :
                          rank === 3 ? 'text-yellow-600' :
                          'text-gray-400'
                        }`}>
                          {rank === 1 ? '🥇' :
                           rank === 2 ? '🥈' :
                           rank === 3 ? '🥉' :
                           `#${rank}`}
                        </span>
                      </div>
                    </td>

                    {/* Player Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">
                        {player.username}
                      </div>
                    </td>

                    {/* Faction */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${getFactionColor(player.faction_id)}`}>
                        {getFactionIcon(player.faction_id)} {getFactionName(player.faction_id)}
                      </div>
                    </td>

                    {/* Games Played */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-300">
                        {player.games_played}
                      </div>
                    </td>

                    {/* Games Won */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className={`text-sm font-semibold ${
                        player.games_won > 0 ? 'text-green-400' : 'text-gray-400'
                      }`}>
                        {player.games_won}
                      </div>
                    </td>
					{/* ADD THIS NEW WIN RATE CELL */}
<td className="px-6 py-4 whitespace-nowrap text-center">
  <div className="text-sm text-gray-300">
    {player.games_played > 0 
      ? `${Math.round((player.games_won / player.games_played) * 100)}%`
      : '0%'
    }
  </div>
</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="text-center">
        <button
          onClick={loadLeaderboard}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center mx-auto"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh Leaderboard
        </button>
      </div>
    </div>
  );
};

export default Leaderboard;